/**
 * Service for syncing offline data with the server
 */
import { getAuthToken } from './api';
import { 
  getPendingReports, 
  getReportImage, 
  updateReportStatus, 
  deleteReport,
  initDatabase
} from './offlineStorage';

const API_BASE_URL = 'https://tsunami-data.robert-9e7.workers.dev/api';

/**
 * Register the background sync with the service worker
 * @returns {Promise<boolean>} Whether the registration was successful
 */
export const registerBackgroundSync = async () => {
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-tsunami-reports');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Background sync registration failed:', error);
    return false;
  }
};

/**
 * Check if background sync is supported
 * @returns {boolean} Whether background sync is supported
 */
export const isBackgroundSyncSupported = () => {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
};

/**
 * Process pending offline reports
 * @returns {Promise<Array>} Results of the sync operations
 */
export const syncOfflineReports = async () => {
  try {
    // Force a log of the database state
    await initDatabase();
    
    const pendingReports = await getPendingReports();
    console.log('syncOfflineReports: Pending reports retrieved:', pendingReports);
    
    if (!pendingReports || pendingReports.length === 0) {
      console.log('No pending reports found to sync');
      return [];
    }

    console.log(`Found ${pendingReports.length} offline reports to sync`);
    
    const results = [];
    const token = await getAuthToken(true);
    
    if (!token) {
      console.error('Unable to get authentication token for syncing');
      throw new Error('Unable to get authentication token for syncing');
    }
    
    // Track processed reports to avoid duplicates
    const processedIds = new Set();
    
    // Process each report one by one
    for (const report of pendingReports) {
      try {
        // Skip if already processed in this batch
        if (processedIds.has(report.id)) {
          console.log(`Skipping duplicate processing of report ${report.id}`);
          continue;
        }
        
        // Add to processed set
        processedIds.add(report.id);
        
        console.log(`Processing report ${report.id}...`, report);
        
        // Skip if the report is not in pending status anymore
        if (report.status !== 'pending') {
          console.log(`Skipping report ${report.id} - not in pending status (${report.status})`);
          continue;
        }
        
        // Update report status to syncing
        try {
          await updateReportStatus(report.id, 'syncing');
        } catch (statusError) {
          console.error(`Error updating status for report ${report.id}:`, statusError);
          // If we can't update status, the report may have been deleted or doesn't exist
          // Skip this report
          continue;
        }
        
        // Get associated image if any
        const imageData = await getReportImage(report.id);
        console.log('Associated image data:', imageData);
        
        // Prepare FormData - use let instead of const so we can reassign it later
        let formData = new FormData();
        
        // Ensure we're using the correct data structure
        const reportData = report.data ? report.data : report;
        console.log('Report data to sync:', reportData);
        
        // Validate the report data has all required fields
        if (!reportData.description) {
          console.error(`Report ${report.id} is missing required field 'description'`);
          await updateReportStatus(report.id, 'failed', 'Missing required field: description')
            .catch(err => console.error(`Failed to update status: ${err.message}`));
          results.push({
            id: report.id,
            success: false,
            error: 'Missing required field: description'
          });
          continue;
        }
        
        // Ensure reported_at exists and is in the correct format
        if (!reportData.reported_at) {
          reportData.reported_at = new Date().toISOString();
          console.log(`Added missing reported_at to report ${report.id}: ${reportData.reported_at}`);
        }
        
        formData.append('data', JSON.stringify(reportData));
        
        // If there's an image, add it to the form data
        if (imageData) {
          try {
            console.log('Adding image to FormData');
            
            // Verify we have valid image data before creating a blob
            if (imageData.imageData && imageData.imageData.byteLength > 0) {
              // Create a proper File object instead of a Blob to ensure content-disposition is set correctly
              const imageBlob = new Blob([imageData.imageData], { type: imageData.type || 'image/jpeg' });
              const fileName = imageData.filename || 'image.jpg';
              
              // Create a File object which will ensure proper Content-Disposition header
              const imageFile = new File([imageBlob], fileName, { 
                type: imageData.type || 'image/jpeg',
                lastModified: new Date().getTime()
              });
              
              // Explicitly remove any existing 'images' entries first
              // This is a workaround as FormData doesn't have a 'remove' method
              const newFormData = new FormData();
              for (const [key, value] of formData.entries()) {
                if (key !== 'images') {
                  newFormData.append(key, value);
                }
              }
              
              // Use the new cleaned FormData
              formData = newFormData;
              
              // Append the image as 'images' field - this must match what the backend expects
              formData.append('images', imageFile);
              
              console.log(`Image added to form data: ${imageBlob.size} bytes, type: ${imageData.type}, name: ${fileName}`);
              
              // Log the FormData entries to verify
              console.log('FormData entries:', Array.from(formData.entries()).map(entry => {
                if (entry[0] === 'images') {
                  return ['images', `File object: ${entry[1].name} (${entry[1].type}, ${entry[1].size} bytes)`];
                }
                return entry;
              }));
              
              // Confirm image was actually added
              let hasImage = false;
              for (const [key, value] of formData.entries()) {
                if (key === 'images') {
                  hasImage = true;
                  console.log('✓ Confirmed image is in FormData:', value instanceof File ? 'Valid File object' : 'Not a File object');
                }
              }
              
              if (!hasImage) {
                console.error('❌ Image was NOT added to FormData properly!');
              }
            } else {
              console.warn('Invalid image data found, skipping image upload');
            }
          } catch (imageError) {
            console.error('Error processing image for upload:', imageError);
            // Continue with the report submission without the image
          }
        }
        
        console.log('Sending report to server...');
        // Send the report to the server
        console.log(`Sending report to server with token: ${token ? 'Valid token' : 'No token'}`);

        // Debug what's in the FormData before sending
        console.log('Final FormData contents before sending:');
        let hasImagesField = false;
        for (const [key, value] of formData.entries()) {
          if (key === 'images') {
            hasImagesField = true;
            console.log(`- ${key}: File object ${value.name} (${value.type}, ${value.size} bytes)`);
          } else {
            console.log(`- ${key}: ${typeof value === 'string' ? value : '[object]'}`);
          }
        }

        if (!hasImagesField && imageData) {
          console.warn('⚠️ Image was expected but not found in FormData!');
        }

        // Do not set Content-Type header - fetch will automatically set it with the boundary for multipart/form-data
        const response = await fetch(`${API_BASE_URL}/reports`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Let the browser set the correct Content-Type with boundary for multipart/form-data
          },
          body: formData
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Server returned error:', response.status, errorText);
          throw new Error(`HTTP error: ${response.status} - ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Server response:', result);
        console.log('Response full details:', {
          success: result.success,
          dataReceived: !!result.data,
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });
        
        if (result.success) {
          // If successful, update status and delete the offline record
          try {
            await updateReportStatus(report.id, 'completed')
              .catch(err => console.warn(`Failed to update status: ${err.message}`));
            
            await deleteReport(report.id)
              .catch(err => console.warn(`Failed to delete report: ${err.message}`));
          } catch (cleanupError) {
            console.warn(`Error cleaning up report ${report.id} after successful sync:`, cleanupError);
            // Continue regardless, as the sync itself was successful
          }
          
          results.push({
            id: report.id,
            success: true,
            data: result.data
          });
          console.log(`Report ${report.id} synced successfully`);
        } else {
          // If failed, update status with error
          try {
            await updateReportStatus(report.id, 'failed', result.error || 'API error')
              .catch(err => console.warn(`Failed to update status: ${err.message}`));
          } catch (statusError) {
            console.warn(`Error updating status for failed report ${report.id}:`, statusError);
          }
          
          results.push({
            id: report.id,
            success: false,
            error: result.error || 'API error'
          });
          console.error(`Failed to sync report ${report.id}:`, result.error || 'API error');
        }
      } catch (error) {
        // Handle sync errors
        console.error(`Error syncing report ${report.id}:`, error);
        
        // Don't mark network errors as failed - they should be retried
        if (error.message.includes('Failed to fetch') || error.message.includes('Network Error')) {
          console.log(`Network error for report ${report.id} - will retry later`);
          results.push({
            id: report.id,
            success: false,
            error: error.message,
            retry: true
          });
        } else {
          // For other errors, mark as failed
          try {
            await updateReportStatus(report.id, 'failed', error.message)
              .catch(err => console.warn(`Failed to update status: ${err.message}`));
          } catch (statusError) {
            console.warn(`Error updating status for failed report ${report.id}:`, statusError);
          }
          
          results.push({
            id: report.id,
            success: false,
            error: error.message
          });
        }
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error syncing offline reports:', error);
    return []; // Return empty array instead of throwing to prevent further errors
  }
};

/**
 * Initialize listeners for online/offline status and service worker messages
 * @returns {Function} A cleanup function to remove the listeners
 */
export const initSyncListeners = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    console.log('Not in browser environment, skipping sync listeners');
    return () => {};
  }

  // Only proceed if service worker API is available
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker API not available, skipping sync listeners');
    return () => {};
  }

  const handleOnline = async () => {
    console.log('Network connection restored, syncing offline reports');
    try {
      if (isBackgroundSyncSupported()) {
        const registered = await registerBackgroundSync();
        console.log(`Background sync ${registered ? 'registered' : 'failed to register'}`);
      } else {
        console.log('Background sync not supported, performing manual sync');
        await syncOfflineReports();
      }
    } catch (error) {
      console.error('Error syncing offline reports:', error);
    }
  };

  // Handle service worker messages
  const handleServiceWorkerMessage = async (event) => {
    try {
      if (!event.data) return;
      
      console.log('Service worker message received:', event.data);
      
      if (event.data.type === 'PROCESS_OFFLINE_REQUESTS') {
        console.log('Received sync request from service worker');
        try {
          await syncOfflineReports();
        } catch (syncError) {
          console.error('Error syncing offline reports:', syncError);
        }
      }
    } catch (error) {
      console.error('Error processing service worker message:', error);
    }
  };

  // Add the listeners
  window.addEventListener('online', handleOnline);
  
  // Register service worker message handler if available
  if (navigator.serviceWorker?.addEventListener) {
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
  } else {
    console.log('Service worker message API not available');
  }
  
  // Check initial status
  if (navigator.onLine) {
    console.log('App started online, checking for pending reports');
    setTimeout(() => {
      handleOnline().catch(err => console.error('Initial sync error:', err));
    }, 2000); // Short delay to allow app to fully initialize
  } else {
    console.log('App started offline, will sync when online');
  }
  
  // Return a cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    if (navigator.serviceWorker?.removeEventListener) {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    }
  };
}; 